/**
 * "Ask the assistant to fix these": the validation problems of a flow, in words, and the
 * message that hands them to the assistant.
 *
 * The same list feeds both views: the manual builder's validation panel (from its live
 * validation map) and the assistant pane (from the flow JSON it holds), so a person sees one
 * count wherever they look and one button that does the same thing.
 */

import { hydrate } from '../../../flow-core/hydrate';
import { validateBuilderScreens } from '../../../flow-core/flowValidation';

const label = (screen, index) => screen?.title || `Screen ${index + 1}`;

/** Meta's messages for the flow as last synced, or none. */
export const metaProblems = (syncStatus) =>
  (syncStatus?.validationErrors?.errors || [])
    .map((issue) => (issue && issue.message ? `WhatsApp: ${issue.message}` : null))
    .filter(Boolean);

/** Builder errors from the manual builder's live validation map, one line each, screen named. */
export const problemsFromValidationMap = (validationMap, screens, syncStatus) => {
  const lines = [];
  const byScreen = validationMap?.byScreen || new Map();
  const byComponent = validationMap?.byComponent || new Map();
  (screens || []).forEach((screen, index) => {
    (byScreen.get(screen.id)?.errors || []).forEach((message) => lines.push(`Screen "${label(screen, index)}": ${message}`));
    byComponent.forEach((entry) => {
      if (entry.screenId !== screen.id) return;
      (entry.errors || []).forEach((message) => lines.push(`Screen "${label(screen, index)}", ${entry.componentType}: ${message}`));
    });
  });
  // Flow-level findings (a screen nothing leads to, a routing rule WhatsApp refuses) already name
  // their screen.
  const flowLevel = validationMap?.flowIssues?.errors || [];
  return [...lines, ...flowLevel, ...metaProblems(syncStatus)];
};

/** Builder errors of a compiled flow, as the assistant pane holds it. Warnings are not problems. */
export const problemsFromFlowJson = (flowJson, syncStatus) => {
  if (!flowJson || !Array.isArray(flowJson.screens) || flowJson.screens.length === 0) return metaProblems(syncStatus);
  let errors = [];
  try {
    errors = validateBuilderScreens(hydrate(flowJson)).errors || [];
  } catch {
    // A flow mid-generation can be momentarily unreadable; no problems is better than a crash.
  }
  // The builder validator already names the screen in each message.
  return [...errors, ...metaProblems(syncStatus)];
};

/** The message the assistant receives. Plain words, one problem per line, nothing else changes. */
export const fixRequestText = (problems) =>
  `Fix these validation problems. Change as little as possible and keep everything else exactly as it is:\n${
    problems.map((problem) => `- ${problem}`).join('\n')}`;
